import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { notify } from "utils/notify";
import { TabView, TabPanel } from "primereact/tabview";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  useUserDetails,
  usePatchUser,
  useVerifyUser,
  useEnableUser,
  useDisableUser,
  useLinkDevice,
  useUnlinkDevice,
  useChangeUserPassword,
  useSendPasswordReset,
  PatchUserRequest,
} from "hooks/useUserDetails";
import UserOverviewTab from "components/users/userDetails/UserOverviewTab";
import UserEditTab from "components/users/userDetails/UserEditTab";
import UserDeviceTab from "components/users/userDetails/UserDeviceTab";
import UserPasswordTab from "components/users/userDetails/UserPasswordTab";
import { LoadingScreen } from "components/shared/LoadingScreen";
import { PageError } from "components/shared/PageError";
import {
  PageContainer,
  PageHeader,
  PageTitle,
  ContentContainer,
  DetailsTabView,
  UserInfoSection,
  UserHeader,
  UserName,
  BadgesRow,
  StatusBadge,
  RoleBadge,
  BackButton,
} from "styles/components/userDetails.styles";

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [editForm, setEditForm] = useState<PatchUserRequest>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [isLinkingDevice, setIsLinkingDevice] = useState(false);
  const [isUnlinkingDevice, setIsUnlinkingDevice] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isChangingPrivilege, setIsChangingPrivilege] = useState(false);

  const { data, isLoading, error, refetch } = useUserDetails(userId);
  const patchUserMutation = usePatchUser();
  const verifyUserMutation = useVerifyUser();
  const enableUserMutation = useEnableUser();
  const disableUserMutation = useDisableUser();
  const linkDeviceMutation = useLinkDevice();
  const unlinkDeviceMutation = useUnlinkDevice();
  const changePasswordMutation = useChangeUserPassword();
  const sendPasswordResetMutation = useSendPasswordReset();

  const user = data?.user;

  useEffect(() => {
    if (user) {
      setEditForm({
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        city: user.city ?? "",
        country: user.country ?? "",
        language: user.language ?? "",
        allowsnotifications: user.allowsnotifications ?? false,
        marketingmaterialok: user.marketingmaterialok ?? false,
        updatesok: user.updatesok ?? false,
      });
    }
  }, [user]);

  const handleSaveEdit = async () => {
    setIsSavingEdit(true);
    try {
      await patchUserMutation.mutateAsync({ userId: userId!, data: editForm });
      notify.success("User details saved successfully.");
    } catch (err: any) {
      notify.error(err?.response?.data?.message || "Failed to update user.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleVerify = () => {
    confirmDialog({
      message: `Mark ${user?.firstname} ${user?.lastname}'s account as verified?`,
      header: "Confirm Verification",
      icon: "pi pi-check-circle",
      accept: async () => {
        setIsVerifying(true);
        try {
          await verifyUserMutation.mutateAsync(userId!);
          notify.success("User account has been verified.");
        } catch (err: any) {
          notify.error(err?.response?.data?.message || "Failed to verify user.");
        } finally {
          setIsVerifying(false);
        }
      },
    });
  };

  const handleToggleStatus = () => {
    const isCurrentlyDisabled = user?.isdisabled;
    confirmDialog({
      message: isCurrentlyDisabled
        ? `Enable ${user?.firstname} ${user?.lastname}'s account?`
        : `Disable ${user?.firstname} ${user?.lastname}'s account? They will no longer be able to log in.`,
      header: isCurrentlyDisabled ? "Confirm Enable" : "Confirm Disable",
      icon: isCurrentlyDisabled ? "pi pi-user" : "pi pi-ban",
      acceptClassName: isCurrentlyDisabled ? undefined : "p-button-danger",
      accept: async () => {
        setIsTogglingStatus(true);
        try {
          if (isCurrentlyDisabled) {
            await enableUserMutation.mutateAsync(userId!);
            notify.success("User account has been enabled.");
          } else {
            await disableUserMutation.mutateAsync(userId!);
            notify.success("User account has been disabled.");
          }
        } catch (err: any) {
          notify.error(err?.response?.data?.message || "Failed to update user status.");
        } finally {
          setIsTogglingStatus(false);
        }
      },
    });
  };

  const handleChangePrivilege = (privilege: "USER" | "ADMIN") => {
    if (!user || privilege === user.privledgelevel) return;
    const promote = privilege === "ADMIN";
    confirmDialog({
      message: promote
        ? `Make ${user.firstname} ${user.lastname} an admin user?`
        : `Remove admin privilege from ${user.firstname} ${user.lastname}?`,
      header: "Confirm Privilege Change",
      icon: "pi pi-shield",
      acceptClassName: promote ? undefined : "p-button-danger",
      accept: async () => {
        setIsChangingPrivilege(true);
        try {
          await patchUserMutation.mutateAsync({
            userId: userId!,
            data: { privledgelevel: privilege },
          });
          notify.success(`User privilege changed to ${privilege}.`);
        } catch (err: any) {
          notify.error(err?.response?.data?.message || "Failed to change user privilege.");
        } finally {
          setIsChangingPrivilege(false);
        }
      },
    });
  };

  const handleLinkDevice = async (serialNumber: string) => {
    setIsLinkingDevice(true);
    try {
      await linkDeviceMutation.mutateAsync({ userId: userId!, serialNumber });
      notify.success(`Device ${serialNumber} linked to user.`);
    } catch (err: any) {
      notify.error(err?.response?.data?.message || "Failed to link device.");
    } finally {
      setIsLinkingDevice(false);
    }
  };

  const handleUnlinkDevice = () => {
    confirmDialog({
      message: `Remove device ${user?.Device?.serialNumber} from this user?`,
      header: "Confirm Unlink Device",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        setIsUnlinkingDevice(true);
        try {
          await unlinkDeviceMutation.mutateAsync(userId!);
          notify.success("Device unlinked successfully.");
        } catch (err: any) {
          notify.error(err?.response?.data?.message || "Failed to unlink device.");
        } finally {
          setIsUnlinkingDevice(false);
        }
      },
    });
  };

  const handleChangePassword = (newPassword: string) => {
    if (newPassword.length < 8) {
      notify.warning("Password must be at least 8 characters.");
      return;
    }
    confirmDialog({
      message: `Change the password for ${user?.firstname} ${user?.lastname}?`,
      header: "Confirm Password Change",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        setIsChangingPassword(true);
        try {
          await changePasswordMutation.mutateAsync({
            userId: userId!,
            newPassword,
          });
          notify.success("User password updated successfully.");
        } catch (err: any) {
          notify.error(err?.response?.data?.message || "Failed to change password.");
        } finally {
          setIsChangingPassword(false);
        }
      },
    });
  };

  const handleSendPasswordReset = () => {
    confirmDialog({
      message: `Send a password reset email to ${user?.email}?`,
      header: "Confirm Password Reset Email",
      icon: "pi pi-envelope",
      accept: async () => {
        setIsSendingReset(true);
        try {
          await sendPasswordResetMutation.mutateAsync(userId!);
          notify.success(`Reset email sent to ${user?.email}.`);
        } catch (err: any) {
          notify.error(err?.response?.data?.message || "Failed to send reset email.");
        } finally {
          setIsSendingReset(false);
        }
      },
    });
  };

  if (error) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>User Details</PageTitle>
          <BackButton
            icon="pi pi-arrow-left"
            onClick={() => navigate("/users")}
            label="Back to Users"
          />
        </PageHeader>
        <PageError
          title="Error loading user"
          message="Please try again later."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ConfirmDialog />

      <PageHeader>
        <PageTitle>User Details</PageTitle>
        <BackButton
          icon="pi pi-arrow-left"
          onClick={() => navigate("/users")}
          label="Back to Users"
        />
      </PageHeader>

      {isLoading ? (
        <LoadingScreen message="Loading user details..." />
      ) : user ? (
        <ContentContainer>
          <UserInfoSection>
            <UserHeader>
              <UserName>
                {user.firstname} {user.lastname}
              </UserName>
              <BadgesRow>
                <RoleBadge role={user.privledgelevel}>
                  {user.privledgelevel?.toLowerCase() || "user"}
                </RoleBadge>
                <StatusBadge status={user.isdisabled ? "disabled" : "active"}>
                  {user.isdisabled ? "Disabled" : "Active"}
                </StatusBadge>
                <StatusBadge status={user.isVerified ? "active" : "inactive"}>
                  {user.isVerified ? "Verified" : "Unverified"}
                </StatusBadge>
              </BadgesRow>
            </UserHeader>
          </UserInfoSection>

          <DetailsTabView>
            <TabView
              activeIndex={activeTab}
              onTabChange={(e) => setActiveTab(e.index)}
            >
              <TabPanel header="Overview">
                <UserOverviewTab
                  user={user}
                  isVerifying={isVerifying}
                  onVerify={handleVerify}
                  isTogglingStatus={isTogglingStatus}
                  onToggleStatus={handleToggleStatus}
                  isChangingPrivilege={isChangingPrivilege}
                  onChangePrivilege={handleChangePrivilege}
                />
              </TabPanel>

              <TabPanel header="Edit Profile">
                <UserEditTab
                  form={editForm}
                  isSaving={isSavingEdit}
                  onChange={setEditForm}
                  onSave={handleSaveEdit}
                />
              </TabPanel>

              <TabPanel header="Device">
                <UserDeviceTab
                  device={user.Device}
                  isLinking={isLinkingDevice}
                  isUnlinking={isUnlinkingDevice}
                  onLink={handleLinkDevice}
                  onUnlink={handleUnlinkDevice}
                />
              </TabPanel>

              <TabPanel header="Password">
                <UserPasswordTab
                  userEmail={user.email}
                  isChangingPassword={isChangingPassword}
                  isSendingReset={isSendingReset}
                  onChangePassword={handleChangePassword}
                  onSendReset={handleSendPasswordReset}
                />
              </TabPanel>
            </TabView>
          </DetailsTabView>
        </ContentContainer>
      ) : (
        <PageError title="User not found" message="This user could not be found." />
      )}
    </PageContainer>
  );
};

export default UserDetails;
